import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type Level = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
export type Color = 'dark' | 'light';
export type Alignment = 'left' | 'center' | 'right';
export type Weight = 'extrabold' | 'semibold' | 'bold' | 'medium';
export type Margin = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

type LevelAlignmentDefinition = {
  [key: string]: Alignment[];
};

export type HeadingProps = {
  /**
   * Text of the button
   */
  text?: string;
  /**
   * Heading level
   */
  level?: Level;
  /**
   * Heading alignment
   */
  align?: Alignment;
  /**
   * Color of heading
   */
  color?: Color;
  /**
   * Bold or semi bold
   */
  weight?: Weight;
  /**
   * Flag to display optional field text
   */
  optionalField?: boolean;
  /**
   * Flag to remove margin
   */
  margin?: Margin;
  /**
   * Should this headline be truncated?
   */
  truncate?: boolean;
};

export const Heading: FC<HeadingProps> = ({
  text = 'Headline',
  level = 'h1',
  align = 'left',
  color = 'dark',
  weight,
  optionalField = false,
  margin,
  truncate,
}: HeadingProps) => {
  // As only certain alignments are allowed for each level, we check here if its a vald one
  const wrongAlignment = useMemo(() => {
    const forbiddenAlignments: LevelAlignmentDefinition = {
      h1: ['right'],
      h2: ['right'],
      h3: [],
      h4: [],
      h5: ['right'],
      h6: [],
    };
    return forbiddenAlignments[level].includes(align);
  }, [level, align]);

  const { t } = useTranslation();
  const [fontWeight, setFontWeight] = useState(
    weight ? `font-${weight}` : 'font-semibold',
  );

  const contentClasses = useMemo(() => {
    // Handle color
    const colorClasses = color === 'dark' ? 'text-gray-700' : 'text-white';

    // Handle level
    let levelClasses = '';
    switch (level) {
      case 'h1':
        levelClasses = `text-4xl md:text-6xl leading-9 md:leading-9${
          typeof margin !== 'undefined' ? ` mb-${margin}` : ' mb-4'
        }`;
        setFontWeight(weight ? `font-${weight}` : 'font-bold');
        break;
      case 'h2':
        levelClasses = `font-sans text-3xl md:text-5xl leading-8 md:leading-8${
          typeof margin !== 'undefined' ? ` mb-${margin}` : ' mb-3'
        }`;
        break;
      case 'h3':
        levelClasses = `font-sans text-xl md:text-2xl leading-6 md:leading-9${
          typeof margin !== 'undefined' ? ` mb-${margin}` : ' mb-3'
        }`;
        break;
      case 'h4':
        levelClasses = `font-sans text-base leading-5${
          typeof margin !== 'undefined' ? ` mb-${margin}` : ' mb-1'
        }`;
        break;
      case 'h5':
        levelClasses = `text-base leading-5`;
        setFontWeight(weight ? `font-${weight}` : 'font-bold');
        break;
      case 'h6':
        levelClasses = `font-sans text-xs leading-4`;
        break;
    }

    // Handle alignment
    let alignmentClasses = '';
    switch (align) {
      case 'left':
        alignmentClasses = wrongAlignment ? '' : 'text-left';
        break;
      case 'center':
        alignmentClasses = wrongAlignment ? '' : 'text-center';
        break;
      case 'right':
        alignmentClasses = wrongAlignment ? '' : 'text-right';
        break;
    }


    return `${colorClasses} ${levelClasses} ${alignmentClasses}`.trim();
  }, [level, color, align, margin, weight, wrongAlignment]);

  const truncateClass = useMemo(() => truncate ? 'truncate' : '', [truncate]);

  return (
    <span className={`${contentClasses} ${fontWeight}`}>
      <h1 className={truncateClass}>
        {text}
        {optionalField && (
          <span className="font-regular"> ({t('labels:optional')})</span>
        )}
      </h1>
    </span>
  );
};
